import { AnimatePresence, motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../utils/cn';

interface AccordionStepProps {
  index: number;
  open: boolean;
  completed: boolean;
  disabled?: boolean;
  title: string;
  summary?: ReactNode;
  onOpen: () => void;
  children: ReactNode;
}

/**
 * One step of the compose accordion. Three visual states:
 *   - disabled  → muted text, no interaction (future step the user can't reach yet)
 *   - completed (collapsed) → shows summary chip with Edit affordance
 *   - open      → shows the full picker
 *
 * Animation: framer-motion AnimatePresence for body expand/collapse.
 */
export const AccordionStep = ({
  index,
  open,
  completed,
  disabled,
  title,
  summary,
  onOpen,
  children,
}: AccordionStepProps) => {
  const { t } = useTranslation('smart');

  return (
    <section
      className={cn(
        'rounded-2xl border transition-colors',
        open
          ? 'border-fg-muted bg-bg-elevated'
          : 'border-border bg-bg-elevated/60',
        disabled && 'opacity-50',
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        disabled={disabled}
        aria-expanded={open}
        className={cn(
          'flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left',
          'focus-visible:ring-accent focus-visible:ring-2 focus-visible:ring-inset focus-visible:outline-none',
          !disabled && 'cursor-pointer',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors',
            completed
              ? 'bg-accent text-accent-fg'
              : open
                ? 'bg-fg text-bg'
                : 'bg-bg-muted text-fg-muted',
          )}
        >
          {completed ? '✓' : index + 1}
        </span>
        <span className="flex flex-1 flex-col">
          <span
            className={cn(
              'text-sm font-semibold leading-tight',
              !open && completed ? 'text-fg-muted' : 'text-fg',
            )}
          >
            {title}
          </span>
          {!open && completed && summary && (
            <span className="text-fg mt-0.5 text-base font-medium leading-tight">
              {summary}
            </span>
          )}
        </span>
        {!open && completed && !disabled && (
          <span className="text-fg-muted text-xs font-medium uppercase tracking-wide">
            {t('wizard.accordion.edit')}
          </span>
        )}
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 0.61, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-5 pt-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
